/** Reminder Contracts API client (docs/REMINDER_CONTRACTS_PLAN.md §5.3, §7). */

export interface ReminderContract {
  id: number
  obligation_text: string
  bound_item_type: string | null
  bound_item_id: number | null
  binding_broken_at: string | null
  created_at: string
  interval_minutes: number
  persistence: 'normal' | 'persistent' | 'aggressive'
  deadline_at: string | null
  op_state: 'waiting' | 'firing' | 'snoozed' | null
  next_fire_at: string | null
  /** Why a waiting contract has no fire time yet: 'time' = said in chat with
   *  no when ("remind me to buy eggs tomorrow"); it arms when the task it
   *  binds to has a clock. 'trigger' = a place trigger. */
  awaiting_reason?: 'time' | 'trigger' | string | null
  /** The bound task's own stated day and clock (2026-09-12), so the strip can
   *  say "tomorrow · no time yet" and keep later days off today's list. */
  bound_due_date?: string | null
  bound_due_time?: string | null
  /** The day the chat ask named when no clock came with it ("tomorrow"),
   *  before any task exists to carry it. */
  stated_due_date?: string | null
  rung: number
  overdue: boolean
  last_fired_at: string | null
  pending_checkin_id: number | null
  critical: boolean
  quiet_hours_mode: 'respect' | 'sovereign'
  quiet_suppressed: boolean
  quiet_resume_at: string | null
  quiet_override_until: string | null
  latest_firing_id: number | null
  latest_firing_acked: boolean
}

const BASE = '/api/reminders/v1'

export async function listActiveReminders(): Promise<ReminderContract[]> {
  const r = await fetch(`${BASE}/active`, { credentials: 'include' })
  if (!r.ok) return []
  const b = await r.json().catch(() => null)
  return b?.success ? (b.contracts as ReminderContract[]) : []
}

/**
 * Does the site-wide critical banner own this contract? (extracted from
 * CriticalReminderBanner 2026-08-29 — defect: the banner and the /today
 * obligation strip each rendered the SAME firing critical contract with
 * the same four actions, ~200px apart.)
 *
 * Fidelity requires the obligation be un-ignorable; it does not require it
 * be un-ignorable twice. The banner is the louder surface, so it keeps the
 * contract and the strip stands down — see ObligationStrip's filter.
 */
export function isBannerCritical(c: ReminderContract): boolean {
  return (
    c.critical &&
    !c.quiet_suppressed &&
    c.latest_firing_id != null &&
    !c.latest_firing_acked &&
    c.op_state !== 'snoozed'
  )
}

/* ------------------------------------------------------------------ *
 * Shared active-contract store (2026-08-29)
 *
 * Defect: /today fetched GET /active four times per load — the banner and
 * the strip each mounted their own poller (and StrictMode doubled both) —
 * and the two pollers ran on different clocks (45s vs 60s), so the two
 * copies of one contract could disagree for up to 15 seconds after an
 * action. One fetch, one interval, one state, every subscriber notified.
 *
 * Cadence is 45s — the TIGHTER of the two former clocks. This poll is what
 * makes a critical firing appear on screen, and fidelity is not something
 * to budget away: slowing to 60s would let a sev1 obligation sit unseen
 * for an extra 15s. It still halves the request rate, because the second
 * poller is gone.
 * ------------------------------------------------------------------ */

export interface ActiveRemindersState {
  contracts: ReminderContract[]
  /** True only when the most recent fetch FAILED — never render an empty
   *  state on this: "nothing is holding you" and "we could not ask" are
   *  different claims. */
  error: boolean
  /** False until the first fetch settles, so callers can stay silent
   *  rather than flash an empty state while in flight. */
  loaded: boolean
}

const ACTIVE_POLL_MS = 45000

let activeState: ActiveRemindersState = { contracts: [], error: false, loaded: false }
const activeListeners = new Set<(s: ActiveRemindersState) => void>()
let activeTimer: number | null = null
let activeInFlight: Promise<void> | null = null
let activeInFlightSeq = 0
let activeSeq = 0
let activeApplied = 0

async function fetchActive(force: boolean): Promise<void> {
  // A refresh AFTER an action must not join a GET that was already in
  // flight when the action landed — that reply predates the change.
  if (activeInFlight && !force) return activeInFlight
  const mySeq = ++activeSeq
  const run = (async () => {
    let next: ActiveRemindersState
    try {
      const r = await fetch(`${BASE}/active`, { credentials: 'include' })
      if (!r.ok) throw new Error(`active reminders: HTTP ${r.status}`)
      const b = await r.json()
      if (!b?.success) throw new Error('active reminders: unsuccessful response')
      next = { contracts: b.contracts as ReminderContract[], error: false, loaded: true }
    } catch {
      // Keep the last known contracts on screen and flag the failure; the
      // caller decides how to say "I could not check".
      next = { contracts: activeState.contracts, error: true, loaded: true }
    }
    if (activeInFlightSeq === mySeq) activeInFlight = null
    if (mySeq < activeApplied) return // a newer fetch already landed
    activeApplied = mySeq
    activeState = next
    activeListeners.forEach((fn) => fn(activeState))
  })()
  activeInFlight = run
  activeInFlightSeq = mySeq
  return run
}

/** Subscribe to the shared active-contract state. Returns an unsubscribe.
 *  The poll interval exists only while at least one subscriber is live. */
export function subscribeActiveReminders(
  fn: (s: ActiveRemindersState) => void,
): () => void {
  activeListeners.add(fn)
  fn(activeState)
  if (activeTimer == null) {
    activeTimer = window.setInterval(() => { void fetchActive(false) }, ACTIVE_POLL_MS)
  }
  void fetchActive(false)
  return () => {
    activeListeners.delete(fn)
    if (activeListeners.size === 0 && activeTimer != null) {
      window.clearInterval(activeTimer)
      activeTimer = null
    }
  }
}

/** Re-read now — call after any action, so every surface moves together. */
export function refreshActiveReminders(): Promise<void> {
  return fetchActive(true)
}

export async function createReminder(input: {
  obligation_text: string
  interval_minutes: number
  persistence?: string
  deadline_at?: string
  /** From when (ISO). Omit for "from now". */
  first_fire_at?: string
  /** Stop nagging at (ISO). Omit for "until it is done". */
  expires_at?: string
  /** respect = pause in quiet hours; sovereign = keep going through them. */
  quiet_hours_mode?: 'respect' | 'sovereign'
  bound_item_type?: string
  bound_item_id?: number
  critical?: boolean
  trigger?: { kind: 'location'; place_id: number; on: 'enter' }
}): Promise<boolean> {
  const r = await fetch(BASE, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  return r.ok
}

export async function reminderAction(
  contractId: number,
  action: 'done' | 'snooze' | 'acknowledge' | 'stop' | 'blocked' | 'dismissed' | 'alert_anyway',
  snoozeTarget?: string,
  extra?: { firing_id?: number; override_minutes?: number },
): Promise<Record<string, unknown> | null> {
  const r = await fetch(`${BASE}/${contractId}/action`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, snooze_target: snoozeTarget, ...extra }),
  })
  return r.json().catch(() => null)
}

export async function answerCheckin(
  contractId: number,
  answer: 'keep' | 'reschedule' | 'no_longer_possible',
): Promise<boolean> {
  const r = await fetch(`${BASE}/${contractId}/checkin`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ answer }),
  })
  return r.ok
}

export async function amendReminder(
  contractId: number,
  changes: { interval_minutes?: number; persistence?: string },
): Promise<boolean> {
  const r = await fetch(`${BASE}/${contractId}/amend`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(changes),
  })
  return r.ok
}

/**
 * Give a waiting contract its time (2026-09-12). `date` is YYYY-MM-DD and
 * `time` HH:MM on the person's own clock; the server resolves the zone (the
 * bound task's snapshot, else the profile) and, when the contract is bound to
 * a task, writes the task's due date and time too — the task stays the one
 * truth and the contract follows it.
 */
export async function setReminderTime(
  contractId: number, date: string, time: string,
): Promise<boolean> {
  const r = await fetch(`${BASE}/${contractId}/time`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ date, time }),
  })
  return r.ok
}

export interface ReminderSettings {
  default_persistence: 'normal' | 'persistent' | 'aggressive'
  aggressive_acknowledged: boolean
  critical_quiet_mode: 'respect' | 'sovereign'
}

export async function getReminderSettings(): Promise<ReminderSettings | null> {
  const r = await fetch(`${BASE}/settings`, { credentials: 'include' })
  if (!r.ok) return null
  const b = await r.json().catch(() => null)
  return b?.success ? b : null
}

export async function setReminderDefault(
  level: string, acknowledge = false,
): Promise<{ success?: boolean; needs_acknowledgement?: boolean; copy?: string } | null> {
  const r = await fetch(`${BASE}/settings`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ default_persistence: level, acknowledge }),
  })
  return r.json().catch(() => null)
}

export async function setCriticalQuietMode(mode: 'respect' | 'sovereign'): Promise<boolean> {
  const r = await fetch(`${BASE}/settings`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ critical_quiet_mode: mode }),
  })
  return r.ok
}
